import { useEffect, useState } from 'react';
import { Zap, Copy, KeyRound, Trash2, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { getQuickAddToken, createQuickAddToken, deleteQuickAddToken } from '../lib/api';
import { friendlyError } from '../lib/errorReporting';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const QUICK_ADD_URL = `${BACKEND_URL}/api/quick-add`;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent || '');

// Settings → "Anotar sin abrir la app". Android: the home-screen icon's
// long-press menu (manifest.json shortcuts). iPhone: an Atajo that posts to
// /api/quick-add with a personal code generated here (backend QUICK ADD).
export default function QuickAddCard() {
  const [info, setInfo] = useState(null);
  const [token, setToken] = useState(null); // only right after generating it
  const [busy, setBusy] = useState(false);
  const ios = isIOS();

  useEffect(() => { getQuickAddToken().then(setInfo).catch(() => setInfo({ exists: false })); }, []);

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copiado`);
    } catch {
      toast.error('No se pudo copiar. Mantén presionado el texto para copiarlo.');
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const out = await createQuickAddToken();
      setToken(out.token);
      setInfo({ exists: true, hint: out.hint, created_at: out.created_at, last_used_at: null });
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo generar el código'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await deleteQuickAddToken();
      setToken(null);
      setInfo({ exists: false });
      toast.success('Código desactivado');
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo desactivar el código'));
    } finally {
      setBusy(false);
    }
  };

  const android = (
    <div className="rounded-xl bg-slate-900/50 p-4">
      <p className="text-sm font-medium text-white mb-1">Android</p>
      <p className="text-sm text-slate-400">
        Mantén presionado el ícono de MiPisto en tu pantalla de inicio y toca <span className="text-slate-200">Anotar gasto</span>:
        se abre directo el formulario. Necesitas tener MiPisto instalada como app.
      </p>
    </div>
  );

  const iphone = (
    <div className="rounded-xl bg-slate-900/50 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium text-white mb-1">iPhone (Atajos)</p>
        <p className="text-sm text-slate-400">
          Crea un Atajo y anota con dos toques en la parte de atrás del iPhone, sin abrir MiPisto.
        </p>
      </div>

      {token ? (
        <div className="space-y-1.5">
          <p className="text-xs text-amber-300">Copia tu código ahora: no lo volveremos a mostrar.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate rounded-lg bg-black/30 px-2.5 py-2 text-xs text-slate-200">{token}</code>
            <button onClick={() => copy(token, 'Código')} className="shrink-0 rounded-lg bg-violet-500 hover:bg-violet-600 text-on-brand px-3 py-2 text-xs font-medium flex items-center gap-1">
              <Copy className="w-3.5 h-3.5" /> Copiar
            </button>
          </div>
        </div>
      ) : info?.exists ? (
        <p className="text-xs text-slate-400">
          Tienes un código activo (termina en <span className="text-slate-200">…{info.hint}</span>)
          {info.last_used_at ? ` · usado por última vez el ${new Date(info.last_used_at).toLocaleDateString()}` : ' · aún sin usar'}.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button onClick={generate} disabled={busy || !info} className="flex items-center gap-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white px-3.5 py-2 text-sm font-medium disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          {info?.exists ? 'Generar código nuevo' : 'Generar mi código'}
        </button>
        {info?.exists && (
          <button onClick={revoke} disabled={busy} className="flex items-center gap-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 px-3.5 py-2 text-sm font-medium disabled:opacity-50">
            <Trash2 className="w-4 h-4" /> Desactivar
          </button>
        )}
      </div>
      {info?.exists && !token && <p className="text-[11px] text-slate-500">Un código nuevo reemplaza al anterior: tendrás que pegarlo otra vez en tu Atajo.</p>}

      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-400">
        <li>Abre la app <span className="text-slate-200">Atajos</span> y crea uno nuevo llamado “Anotar gasto”.</li>
        <li>Agrega <span className="text-slate-200">Dictar texto</span> (o <span className="text-slate-200">Solicitar entrada</span> si prefieres escribir).</li>
        <li>
          Agrega <span className="text-slate-200">Obtener contenido de URL</span> con esta dirección:
          <span className="mt-1 flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate rounded-lg bg-black/30 px-2 py-1 text-xs text-slate-200">{QUICK_ADD_URL}</code>
            <button onClick={() => copy(QUICK_ADD_URL, 'Dirección')} className="shrink-0 text-violet-400 hover:text-violet-300"><Copy className="w-3.5 h-3.5" /></button>
          </span>
          Toca <span className="text-slate-200">Mostrar más</span>: Método <span className="text-slate-200">POST</span>; en Encabezados agrega
          <span className="text-slate-200"> Authorization</span> con el valor <span className="text-slate-200">Bearer </span>+ tu código
          (con un espacio después de Bearer); en Cuerpo elige <span className="text-slate-200">JSON</span> y agrega el campo
          <span className="text-slate-200"> texto</span> con el texto dictado.
        </li>
        <li>Agrega <span className="text-slate-200">Obtener valor de diccionario</span> con la clave <span className="text-slate-200">mensaje</span> y después <span className="text-slate-200">Mostrar resultado</span>.</li>
      </ol>
      <div className="rounded-lg bg-violet-500/10 border border-violet-500/20 p-3">
        <p className="text-sm font-medium text-white mb-1">Con dos toques atrás del iPhone</p>
        <p className="text-sm text-slate-400">
          Ve a <span className="text-slate-200">Ajustes → Accesibilidad → Tocar → Toque posterior → Doble toque</span> y
          elige tu atajo <span className="text-slate-200">Anotar gasto</span>. Toca dos veces la parte de atrás del
          iPhone, di algo como “3.50 Starbucks” y listo: MiPisto elige la categoría por ti.
        </p>
      </div>
      <p className="text-xs text-slate-500">
        También funciona con “Oye Siri, anotar gasto” o desde un ícono en tu pantalla de inicio.
      </p>
    </div>
  );

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="quick-add-card">
      <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
        <Zap className="w-5 h-5 text-violet-400" />
        Anotar sin abrir la app
      </h3>
      <p className="text-sm text-slate-400 mb-4 flex items-center gap-1.5">
        <Smartphone className="w-4 h-4 shrink-0" /> Anota un gasto en segundos desde tu pantalla de inicio.
      </p>
      <div className="space-y-3">
        {ios ? <>{iphone}{android}</> : <>{android}{iphone}</>}
      </div>
    </div>
  );
}
